import { KdsCardFile, KdsDropfile } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { type ContextType } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { type FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import {
  type OrderGetOrderDetailsOrderItemReadDto,
  type OrderItemDetailsReadDto,
} from "@/config/apiClient";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { type PreMediaClientRequestDetailsFormSchema } from "@/modals/ModalPreMediaClientRequestDetailsForm";

type OrderItem = OrderItemDetailsReadDto | OrderGetOrderDetailsOrderItemReadDto;

interface PreMediaUploadFieldProps {
  id: string;
  index: number;
  orderItem: OrderItem;
  isUploadingFile: boolean;
  onFileUpload: (
    file: File,
    index: number,
    ctx: ContextType<typeof FileContext>
  ) => Promise<void>;
  ctx: ContextType<typeof FileContext>;
}

export const PreMediaUploadField = ({
  index,
  orderItem,
  isUploadingFile,
  onFileUpload,
  ctx,
}: PreMediaUploadFieldProps) => {
  const { t } = useTranslate();
  const methods = useFormContext<PreMediaClientRequestDetailsFormSchema>();
  const isDeleted = useWatch({
    control: methods.control,
    name: `preMediaFiles.${index}.isDeleted`,
  });

  const existingFile = orderItem.sandboxItem?.preMediaFiles?.[index];

  if (existingFile?.blobId && isDeleted) {
    return null;
  }

  if (existingFile?.blobId && !isDeleted) {
    return (
      <KdsCardFile
        fileName={existingFile.fileName}
        fileUrl={existingFile.url}
        isViewable
        isDeletable
        onDelete={() =>
          methods.setValue(`preMediaFiles.${index}.isDeleted`, true)
        }
      />
    );
  }

  return (
    <Controller
      name={`preMediaFiles.${index}.fileSignedId`}
      render={({ field, fieldState }) => (
        <KdsDropfile
          dataCy={`preMediaFilesUpload-${index}`}
          errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
          hasError={!!fieldState.error}
          isDeletable={!!field.value}
          isLoading={isUploadingFile}
          isReadable={!!field.value}
          maxSize={SIZE_5G}
          onDelete={() => {
            if (methods.getValues("preMediaFiles").length > 1) {
              methods.setValue("preMediaFiles", [
                ...methods
                  .getValues("preMediaFiles")
                  .filter((_, i) => i !== index),
              ]);
            }
          }}
          onUploadError={() => {
            if (methods.getValues("preMediaFiles").length > 1) {
              methods.setValue("preMediaFiles", [
                ...methods
                  .getValues("preMediaFiles")
                  .filter((_, i) => i !== index),
              ]);
            }
          }}
          onUploadStart={() => {
            methods.clearErrors(`preMediaFiles.${index}.fileSignedId`);
            methods.setValue("preMediaFiles", [
              ...methods.getValues("preMediaFiles"),
              {
                fileSignedId: "",
                isDeleted: false,
                blobId: "",
              },
            ]);
          }}
          onUpload={(file) => void onFileUpload(file, index, ctx)}
          placeholderDescription={t(
            "order.catalogMediumConfiguration.stepVersions.uploadSize"
          )}
          placeholderTitle={t("fileUpload.uploadInfo")}
        />
      )}
    />
  );
};
